/* eslint-disable no-prototype-builtins */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Avatar, Button, LoadingButton } from '@olxbr/ds-core'
import { Link } from '@olxbr/ds-web'

import { View } from '../../atoms/View'
import Paper from '../../atoms/Paper'
import ScrollView from '../../atoms/ScrollView'
import PasswordInput from '../../molecules/PasswordInput'
import CapsLockTooltip from '../../molecules/CapsLockTooltip'
import EmailChip from '../../atoms/EmailChip'
import TermsAndPrivacyNotice from '../../molecules/TermsAndPrivacyNotice'
import { isLoginScrollEnabled } from '../../../util/experiments'
import { breakpoints } from '../../../util/medias'

const DESKTOP_BP = `${breakpoints.tablet + 1}px`

const getContentElement = ({ modalMode, ...props }) =>
  modalMode ? <View {...props} /> : <Paper {...props} />

const FormContent = styled(View)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const PageForm = styled.div`
  margin-top: var(--spacing-1);
`

const Content = styled(View)`
  text-align: center;
  margin: auto;
  ${({ modalMode }) =>
    !modalMode &&
    `
    margin-top: var(--spacing-8);
    margin-bottom: var(--spacing-8);
  `}
  max-width: 466px;
`

const PasswordInputWrapper = styled(View)`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-2);
`

const CustomPaper = styled(getContentElement)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const FullWidth = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-3);
  padding-left: var(--spacing-2);
  padding-right: var(--spacing-2);
  width: 100%;
`

const NewPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 88px);
  overflow: hidden;
  @media (min-width: ${DESKTOP_BP}) {
    margin-top: var(--spacing-6);
    height: auto;
    overflow: visible;
    display: block;
  }
`

const TwoColumnWrapper = styled.div`
  margin: 0 auto;
  flex: 1;
  @media (min-width: ${DESKTOP_BP}) {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    max-width: 1040px;
    padding: var(--spacing-6) var(--spacing-4) 0;
    gap: var(--spacing-7);
  }
`

const LeftColumn = styled.div`
  display: none;
  @media (min-width: ${DESKTOP_BP}) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
    max-width: 469px;
  }
`

const RightColumn = styled.div`
  @media (min-width: ${DESKTOP_BP}) {
    width: 504px;
    flex-shrink: 0;
  }
`

const NewMobileHeader = styled.div`
  padding: var(--spacing-3) var(--spacing-2) 0;
  @media (min-width: ${DESKTOP_BP}) {
    display: none;
  }
`

const NewCardContent = styled.div`
  padding: var(--spacing-2);
  @media (min-width: ${DESKTOP_BP}) {
    border: var(--border-width-0-1) solid var(--container-border-color-outlined);
    border-radius: var(--border-radius-1);
    padding: var(--spacing-4) var(--spacing-7);
  }
`

const NewDesktopFooter = styled.div`
  margin-top: auto;
  padding-top: var(--spacing-7);
`

const NewMobileFooter = styled.div`
  display: flex;
  justify-content: center;
  max-width: 466px;
  margin: auto;
  margin-top: auto;
  text-align: center;
  padding: var(--spacing-1) var(--spacing-2);
  @media (min-width: ${DESKTOP_BP}) {
    display: none;
  }
`

const HideOnMobile = styled.span`
  @media (max-width: ${parseInt(DESKTOP_BP) - 1}px) {
    display: none;
  }
`

const PasswordLoginTemplate = ({
  abTests,
  email,
  onSubmit,
  onForgotPassword,
  onBackClick,
  isSubmitting,
  shouldDisableSubmit,
  inputWrapper,
  errorMsg,
  captchaChallenge,
  lurker,
  modalMode,
  onPrivacyLinkClick,
  onTermsLinkClick,
}) => {
  const [showCapsOnTooltip, setShowCapsOnTooltip] = useState(false)
  const [isCapsOn, setIsCapsOn] = useState(false)
  const experimentOn = isLoginScrollEnabled(abTests)

  const addKeyboardListeners = () => {
    document.addEventListener('keydown', onKeyPress)
    document.addEventListener('keyup', onKeyPress)
  }

  const removeKeyboardListeners = () => {
    document.removeEventListener('keydown', onKeyPress)
    document.removeEventListener('keyup', onKeyPress)
  }

  const onPasswordKeyPress = event => {
    const isCapsOn = event.getModifierState && event.getModifierState('CapsLock')
    setIsCapsOn(isCapsOn)
    setShowCapsOnTooltip(isCapsOn)
  }

  const onKeyPress = event => {
    const isCapsOn = event.getModifierState && event.getModifierState('CapsLock')
    setIsCapsOn(isCapsOn)
  }

  useEffect(() => {
    addKeyboardListeners()

    return () => {
      removeKeyboardListeners()
    }
  }, [])

  const passwordFormContent = (
    <>
      <PasswordInputWrapper>
        {inputWrapper(({ field, fieldState: { error, ...restFields }, ref }) => (
          <PasswordInput
            key="password"
            label="Senha"
            aria-label="Senha"
            errorMsg={'Campo obrigatório. Informe sua senha.'}
            onKeyUp={onPasswordKeyPress}
            onKeyDown={onPasswordKeyPress}
            disabled={isSubmitting}
            maxLength={40}
            {...field}
            {...restFields}
            hasError={!!error}
            ref={ref}
          />
        ))}
        {isCapsOn && showCapsOnTooltip && (
          <CapsLockTooltip message="caps lock ativado" highlight="ativado" lurker={lurker} />
        )}
      </PasswordInputWrapper>
      {captchaChallenge || null}
    </>
  )

  const backAndForgotLinks = (
    <FullWidth>
      {!isSubmitting ? (
        <Link size="medium" onClick={onBackClick}>
          Voltar
        </Link>
      ) : (
        <span className="typo-body-small text-neutral-100">Voltar</span>
      )}
      {!isSubmitting ? (
        <Link size="medium" onClick={onForgotPassword}>
          Esqueceu sua senha?
        </Link>
      ) : (
        <span className="typo-body-small text-neutral-100">Esqueceu sua senha?</span>
      )}
    </FullWidth>
  )

  if (experimentOn && !modalMode) {
    return (
      <NewPageWrapper>
        <TwoColumnWrapper>
          <LeftColumn>
            <div>
              <span className="typo-body-large mb-2" style={{ fontSize: '32px' }}>
                Agora, digite a senha da sua conta
              </span>
              <div className="flex items-center gap-1">
                <Avatar size="small" />
                <span className="typo-body-medium font-bold">{email}</span>
              </div>
            </div>
            <NewDesktopFooter>
              <TermsAndPrivacyNotice
                onPrivacyClick={onPrivacyLinkClick}
                onTermsClick={onTermsLinkClick}
              />
            </NewDesktopFooter>
          </LeftColumn>
          <RightColumn>
            <NewMobileHeader>
              <span className="typo-title-medium mb-2">Agora, digite a sua senha</span>
              <div className="flex items-center gap-1">
                <Avatar size="small" />
                <span className="typo-body-medium font-bold">{email}</span>
              </div>
            </NewMobileHeader>
            <NewCardContent>
              {errorMsg}
              <PageForm method="POST" onSubmit={onSubmit} noValidate>
                {passwordFormContent}
                <LoadingButton
                  disabled={shouldDisableSubmit}
                  isLoading={isSubmitting}
                  onClick={onSubmit}
                  className="w-full"
                  size="medium"
                >
                  Continuar
                </LoadingButton>
              </PageForm>
              <FullWidth>
                <HideOnMobile>
                  {!isSubmitting ? (
                    <Link size="medium" onClick={onBackClick}>
                      Voltar
                    </Link>
                  ) : (
                    <span className="typo-body-small text-neutral-100">Voltar</span>
                  )}
                </HideOnMobile>
                {!isSubmitting ? (
                  <Link size="medium" onClick={onForgotPassword}>
                    Esqueceu sua senha?
                  </Link>
                ) : (
                  <span className="typo-body-small text-neutral-100">Esqueceu sua senha?</span>
                )}
              </FullWidth>
            </NewCardContent>
          </RightColumn>
        </TwoColumnWrapper>
        <NewMobileFooter>
          <TermsAndPrivacyNotice
            onPrivacyClick={onPrivacyLinkClick}
            onTermsClick={onTermsLinkClick}
          />
        </NewMobileFooter>
      </NewPageWrapper>
    )
  }

  return (
    <ScrollView>
      <Content modalMode={modalMode}>
        <CustomPaper modalMode={modalMode}>
          <span className="typo-title-medium">Acesse a sua conta</span>
          <div className="flex mt-3">
            <EmailChip>{email}</EmailChip>
          </div>
          <FormContent>
            {errorMsg}
            <PageForm method="POST" onSubmit={onSubmit} noValidate>
              {passwordFormContent}
              <LoadingButton
                disabled={shouldDisableSubmit}
                isLoading={isSubmitting}
                onClick={onSubmit}
                className="w-full"
              >
                Entrar
              </LoadingButton>
            </PageForm>
          </FormContent>
          {backAndForgotLinks}
        </CustomPaper>
      </Content>
    </ScrollView>
  )
}

PasswordLoginTemplate.propTypes = {
  abTests: PropTypes.string,
  email: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onBackClick: PropTypes.func,
  isSubmitting: PropTypes.bool,
  shouldDisableSubmit: PropTypes.bool,
  errorMsg: PropTypes.element,
  captchaChallenge: PropTypes.element,
  lurker: PropTypes.object,
  modalMode: PropTypes.bool,
  onPrivacyLinkClick: PropTypes.func,
  onTermsLinkClick: PropTypes.func,
}

export { PasswordLoginTemplate }
